/**
 * Analytics module — migrated from Solid CMS.
 *
 * Provides trackPageView, trackComponent, trackComponentLoad and firePageLoadEvent.
 * These are the main public APIs consumed by Next.js pages/components.
 */
"use client";

import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getPageType } from "@repo/shared-utils/common-utils";
import {
	FULLY_LOADED,
	buildDataLayerObject,
	composeCleverTapPayload,
	composeDataLayerProps,
	deriveGaEventType,
	dispatchAnalyticsEvent,
	markViewEventTriggered,
	toErrorMessage,
} from "./functions";
import { waitForPageLoadRequestsToSettle } from "./trackPageLoadRequests";
import type { ComponentEventInput, GaEventType, PageViewInput } from "./types";

const getDomContentLoadedEndMs = (): number | null => {
	if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
		return null;
	}
	const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
	if (!nav) return null;
	const ms = nav.domContentLoadedEventEnd - nav.startTime;
	return Number.isFinite(ms) ? ms : null;
};

export const trackPageView = async (input?: PageViewInput): Promise<void> => {
	const eventType: GaEventType = "view";
	const eventName = input?.eventName ?? getPageType("tag-7");
	const gaEventType = input?.gaEventType ?? eventType;
	try {
		const dataLayerObject = await buildDataLayerObject({
			event: gaEventType,
			event_type: eventType,
			component: "page",
			page_load_type: input?.pageLoadType,
			page_load_time: input?.pageLoadTime,
		});

		const ctPayload = composeCleverTapPayload(
			{
				eventName,
				componentName: "page",
				eventType,
				cleverTapOverrides: input?.cleverTapOverrides,
			},
			dataLayerObject,
		);

		markViewEventTriggered();

		await dispatchAnalyticsEvent(eventName, ctPayload, dataLayerObject, {
			componentName: "page",
			gaEventType,
			gaOverrides: input?.gaOverrides,
		});
	} catch (error) {
		const errorMessage = toErrorMessage(error);
		logErrorToNewRelic("Failed to track page view", {
			requestedEvent: eventName,
			error: errorMessage,
		});
		console.error("[Analytics] page view dispatch failed", error);
	}
};

export const trackComponent = async (input: ComponentEventInput): Promise<void> => {
	if (!input.componentName) {
		const eventInput = JSON.stringify(input);
		logErrorToNewRelic("Analytics.trackComponent called without componentName", { eventInput });
		console.error("Analytics.trackComponent called without componentName. Event dropped.", input);
		return;
	}

	const eventName = input.eventName ?? getPageType("tag-8");

	try {
		const gaEventType = deriveGaEventType(input.gaEventType, input.eventType);
		const dataLayerProps = composeDataLayerProps(input, gaEventType);
		const dataLayerObject = await buildDataLayerObject(dataLayerProps);
		const ctPayload = composeCleverTapPayload({ ...input, eventName }, dataLayerObject);

		await dispatchAnalyticsEvent(eventName, ctPayload, dataLayerObject, {
			componentName: input.componentName,
			destinationUrl: input.destinationUrl,
			position: input.position,
			gaEventType,
			gaOverrides: input.gaOverrides,
		});
	} catch (error) {
		const errorMessage = toErrorMessage(error);
		logErrorToNewRelic("Failed to track component analytics", {
			eventName,
			componentName: input.componentName,
			error: errorMessage,
		});
		console.error("[Analytics] component dispatch failed", error);
	}
};

export const trackComponentLoad = (
	input: Omit<ComponentEventInput, "eventName"> & { eventName?: string },
): Promise<void> => {
	const componentName = input.componentName;

	return trackComponent({
		...input,
		componentName,
		eventName: input.eventName,
		eventType: input.eventType ?? "load",
		clickType: input.clickType ?? "component_load",
	});
};

export const firePageLoadEvent = (): void => {
	if (globalThis.window === undefined || typeof document === "undefined") {
		return;
	}

	type AnalyticsDebugWindow = Window & {
		__BFL_ANALYTICS_DEBUG__?: { console: boolean; fireEvents: boolean };
		__BFL_E2E_PAGE_LOAD_TIME_SECONDS__?: number;
	};

	(globalThis.window as AnalyticsDebugWindow).__BFL_ANALYTICS_DEBUG__ = { console: false, fireEvents: true };
	const pageViewEventName = getPageType("tag-7");

	let fullyLoadedFired = false;
	const fireFullyLoaded = async () => {
		if (fullyLoadedFired) return;
		fullyLoadedFired = true;
		const settleResult = await waitForPageLoadRequestsToSettle();
		const dclMs = getDomContentLoadedEndMs();
		// True end-to-end: time until BOTH doc is ready (DCL end) and initial APIs are complete (apiBatchEndPerfMs).
		const endToEndMs = dclMs === null ? null : Math.max(dclMs, settleResult.apiBatchEndPerfMs);
		const totalSeconds = endToEndMs === null ? null : endToEndMs / 1000;
		if (totalSeconds !== null) {
			(globalThis.window as AnalyticsDebugWindow).__BFL_E2E_PAGE_LOAD_TIME_SECONDS__ = totalSeconds;
		}
		trackPageView({
			eventName: pageViewEventName,
			pageLoadType: FULLY_LOADED,
			...(totalSeconds === null ? {} : { pageLoadTime: `${totalSeconds.toFixed(2)} SECONDS` }),
		});
	};

	if (document.readyState === "complete") {
		fireFullyLoaded();
	} else {
		globalThis.window.addEventListener("load", fireFullyLoaded, { once: true });
	}
};
